/**
 * Export CSV : le fichier est produit par le SERVEUR (D-25), par la même
 * requête que le tableau, avec les mêmes paramètres et la langue courante.
 * Le front ne construit aucun CSV : il télécharge ce qu'il reçoit.
 */
import React, { useRef, useState } from 'react';
import { App, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ExportDataset, ExportParams } from '../../../api/monitoring/v2';
import { useMonitoringV2Api } from '../../../api/monitoring/useMonitoringV2Api';
import { toLangCode } from './format';

export type ExportButtonProps = {
    dataset: ExportDataset;
    params: Omit<ExportParams, 'lang'>;
    disabled?: boolean;
};

/** Déclenche l'enregistrement d'un fichier reçu. */
export const saveBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const ExportButton: React.FC<ExportButtonProps> = ({ dataset, params, disabled }) => {
    const { t, i18n } = useTranslation();
    const { message } = App.useApp();
    const api = useMonitoringV2Api();
    // Identité instable des fonctions d'API : lecture par ref.
    const apiRef = useRef(api);
    apiRef.current = api;
    const [busy, setBusy] = useState(false);

    const handleClick = async () => {
        setBusy(true);
        try {
            const lang = toLangCode(i18n.resolvedLanguage ?? i18n.language);
            const file = await apiRef.current.exportDataset(dataset, { ...params, lang });
            saveBlob(file.blob, file.filename);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[monitoring] export impossible', dataset, err);
            message.error(t('monitoring.export.error'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button size="small" icon={<DownloadOutlined />} onClick={handleClick} loading={busy} disabled={disabled}>
            {t('monitoring.export.button')}
        </Button>
    );
};

export default ExportButton;
